import React from 'react';
import { useOverviewContext } from '../../context/OverviewContext';
import MarkTopicAsReadButton from '../../components/MarkTopicAsReadButton';
import Header from '../../Layout/Header';
import Sidebar from '../../Layout/Sidebar';
import config from '../../config';
import { FooterMobile } from '../../Layout/FooterMobile';
import { getCurrentChecklistId } from '../../utils/checklistUtils';
import { useChecklist } from '../../context/ChecklistContext';

const EmailBasics: React.FC = () => {
    const { HOME, SCHOOL_PREP_CHECKLIST, EMAIL_SETUP } = config.app.routes;

    const {userRoleVal, formattedStudentNames} = useOverviewContext();
    // console.log(userRoleVal);
    
    // Get checklist data & currentChecklistId from ChecklistContext
    const { checklistData } = useChecklist();
    const currentChecklistId = getCurrentChecklistId(checklistData, "EMAIL_BASICS");
    
    return (
        <div className="home-layout">
            <div className="sidebar-wrapper">
            <Sidebar />
            </div>
            <div className="body-wrapper-content">
                <div className="row">
                    <div className="col-12">
                        <Header />
                        <main id="main" role="main" className="main-content">
                            <div className="row">
                                <div className="col-12">
                                    <nav aria-label="breadcrumb" className="d-none d-sm-block">
                                        <ol className="breadcrumb">
                                            <li className="breadcrumb-item">
                                                <a href={HOME}>Home</a>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <a href={SCHOOL_PREP_CHECKLIST}>School Prep Checklist</a>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <a href={EMAIL_SETUP}>School Email</a>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <span>Email Basics</span>
                                            </li>
                                        </ol>
                                    </nav>
                                </div>
                            </div>

                            <h1 className="wp-block-heading">Email Basics</h1>

                            <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-2 wp-block-columns-is-layout-flex">
                                <div className="wp-block-column" style={{ flexBasis: "66.66%" }}>
                                    <h4 className="wp-block-heading">
                                        Email is the primary method of communication between families and their school.
                                        Here’s what you need to know about how school email works.
                                    </h4>
                                    <div style={{ height: "40px" }} aria-hidden="true" className="wp-block-spacer"></div>
                                    
                                    {userRoleVal && userRoleVal.toUpperCase() !== "STUDENT" && ( 
                                        <div className="wp-block-k12-user-role-content-wrapper">
                                            <h3 className="wp-block-heading">Learning Coach Email</h3>
                                            <p>
                                                <strong>Good news—you can use your already existing email for school communications.</strong>
                                            </p>
                                            <p>
                                                You will automatically receive copies of all messages sent to your student at the email address you provided on your enrollment application.
                                            </p>
                                            <p>
                                                Learning Coaches can also use the “Contact Teachers” feature within their OLS account to send messages to teachers and school staff. When you use this email pop-up, you’ll automatically receive a copy of the message you sent in your personal email inbox.
                                            </p>
                                            <div
                                                style={{ height: '40px' }}
                                                aria-hidden="true"
                                                className="wp-block-spacer">
                                            
                                            </div>
                                        </div>
                                    )}

                                    <h3 className="wp-block-heading">Student Email</h3>

                                    <p>
                                        <strong>
                                            Students are automatically assigned a school email address powered by Office 365. To access
                                            student email, first complete this one-time set-up process:
                                        </strong>
                                    </p>

                                    <ul className="wp-block-list">
                                        <li>
                                            Go to <a href="https://office.com/" target="_blank" rel="noreferrer noopener">https://office.com</a>
                                        </li>
                                        <li>Enter student OLS username and password</li>
                                    </ul>

                                    <p>
                                        <strong>
                                            Reminder: Log in to Office 365 with <em>student</em> username and password created for OLS and
                                            this welcome site.
                                        </strong>
                                    </p>

                                    <p>
                                        Once you’ve completed this set-up process, you can access school email in the Online School (OLS). You
                                        can find your full email address on the My Account page at the top of this site, or in the OLS.
                                    </p>

                                    <p>
                                        You can send messages by using the “Contact My Teachers” feature within the OLS and will receive new
                                        message notifications. Note: School email is limited to communicating with teachers, LCs, and school
                                        administrators. Messages will not be delivered to other students, friends, or any outside email address.
                                    </p>

                                    <div style={{ height: "40px" }} aria-hidden="true" className="wp-block-spacer"></div>

                                    <div className="wp-block-k12-section container-fluid bg-color-light-blue-3">
                                        <div className="row">
                                            <div className="col-12 g-0">
                                                <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-1 gap-0 wp-block-columns-is-layout-flex align-items-end">
                                                    <div
                                                        className="wp-block-column p-2rem"
                                                        style={{ flexBasis: "66.66%" }}
                                                    >
                                                        <h2 className="wp-block-heading">Email Every Day</h2>
                                                        <p>
                                                            Since most school communication will come through your email, it’s important to read
                                                            and respond to messages every day. Get in a good habit of checking for new email and
                                                            replying promptly.
                                                        </p>
                                                    </div>
                                                    <div
                                                        className="wp-block-column is-vertically-aligned-bottom d-flex align-items-end h-100 justify-content-end"
                                                        style={{ flexBasis: "33.33%" }}
                                                    >
                                                        <figure className="wp-block-image size-full is-resized mb-0">
                                                            <img
                                                                width="700"
                                                                height="480"
                                                                src="https://welcome.k12.com/wp-content/uploads/2024/06/Image_EmailEveryDay-350x240-1.png"
                                                                alt="Email Every Day"
                                                                className="wp-image-409"
                                                                style={{ width: "270px", height: "auto" }}
                                                            />
                                                        </figure>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="wp-block-column" style={{ flexBasis: "33.33%" }}></div>
                            </div>

                            {/* <MarkTopicAsReadButton checklistId="107" /> */}
                            {currentChecklistId && <MarkTopicAsReadButton checklistId={currentChecklistId} />}


                        </main>
                    </div>
                </div>
            </div>
            <FooterMobile />
        </div>
    );
};

export default EmailBasics;
